import React, { useEffect, useState } from "react";
import styles from "./Login.module.scss";
import backgroundImg from "../../common/images/CA0A1008.png";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../auth.actions";
import {
  useHistory,
  useParams,
  useLocation,
  Redirect,
  Link,
} from "react-router-dom";
import { ReactComponent as Logo } from "../../common/images/Curves_Logotype_White.svg";
import { createSelector } from "reselect";
import { IApplicationState } from "../../redux/root-reducer";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../common/components/LanguageSwitcher";
import { InstallPWA } from "PWA/PWAPrompt";
import {
  TextField,
  Button,
  CircularProgress,
  Typography,
  Box,
} from "@material-ui/core";
import { ResetPassword } from "./ResetPassword";

export interface ILoginState {
  loginFailed: boolean;
  loggingIn: boolean;
}
const getAuthState = createSelector(
  (state: IApplicationState) => state.auth,
  (authItems) => ({
    user: authItems.currentUser,
    loginFailed: authItems.loginFailed,
    loggingIn: authItems.isLoggingIn,
    mobileAppUnauthorized: authItems.mobileAppUnauthorized,
  })
);

export const Login = () => {
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const { loginFailed, loggingIn, user, mobileAppUnauthorized } =
    useSelector(getAuthState);
  const history = useHistory();
  const { domain }: any = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const onLogin = () => {
    dispatch(login(username, password, history, domain, location));
  };

  const urlSearchParams = new URLSearchParams(location.search);
  const urlId = urlSearchParams.get("id");

  useEffect(() => {
    if (urlId) {
      try {
        const userObject = JSON.parse(atob(urlId));
        dispatch(
          login(
            userObject.username,
            userObject.password,
            history,
            domain,
            location
          )
        );
      } catch {}
    }
  }, []);

  const onKeyDown = (e: any) => {
    if (e.key === "Enter") {
      onLogin();
    }
  };

  const renderErrorMessage = () => {
    if (mobileAppUnauthorized) {
      return (
        <p className={styles.errorText}>
          {t(
            "Your account is not verified to use Curves Mobile, please contact your landlord"
          )}
        </p>
      );
    } else if (loginFailed) {
      return (
        <p className={styles.errorText}>
          {t("The username and password you entered do not match")}
        </p>
      );
    } else return null;
  };

  if (user) return <Redirect to="/" />;
  return (
    <div className={styles.loginContainer}>
      <img alt="background" src={backgroundImg} className={styles.bg} />
      <Logo />
      <LanguageSwitcher />
      <div className={styles.form}>
        <form autoComplete="on" onSubmit={onLogin}>
          <div className={styles.formItem}>
            <TextField
              label={domain ? t("Object-nr") : t("Username")}
              fullWidth
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="filled"
              type="email"
              className={styles.input}
            />
          </div>
          <div className={styles.formItem}>
            <TextField
              label={t("Password")}
              fullWidth
              type="password"
              variant="filled"
              onKeyDown={onKeyDown}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
            />
            {renderErrorMessage()}
          </div>
          <div className={styles.formItem}>
            <Button
              size="large"
              onClick={onLogin}
              fullWidth
              color="primary"
              disabled={loggingIn}
              variant="contained"
            >
              {loggingIn ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                t("Sign in")
              )}
            </Button>
          </div>
          {domain && (
            <div className={styles.formItem}>
              <Button
                variant="text"
                size="small"
                onClick={() => setResetPasswordOpen(true)}
                style={{ color: "white", textTransform: "none" }}
              >
                {t("Forgot password?")}
              </Button>
            </div>
          )}
        </form>
      </div>
      <div className={styles.tos}>
        <Typography variant="body2">
          <a
            href="https://ecoguard.se/integritetspolicy/"
            className={styles.privacyStatement}
          >
            {t(
              "By proceeding you also agree to the Terms of Service and Privacy Policy."
            )}
          </a>
        </Typography>
        <Box display="flex" justifyContent="center">
          <Typography variant="body2">
            {process.env.REACT_APP_VERSION}
          </Typography>
        </Box>
      </div>
      <InstallPWA />
      {domain && (
        <ResetPassword
          open={resetPasswordOpen}
          onClose={() => setResetPasswordOpen(false)}
          domainCode={domain}
        />
      )}
    </div>
  );
};
